<script lang="ts">
  import type { HTMLAttributes } from 'svelte/elements'
  import type { Snippet } from 'svelte'
  import { setContext } from 'svelte'
  import { writable, type Writable } from 'svelte/store'

  export type ToggleGroupContext = {
    name: Writable<string>
    type: Writable<'multiple' | 'single'>
    selected: Writable<string[] | string | null>
  }

  type Props = HTMLAttributes<HTMLDivElement> & {
    children: Snippet
    name: string
    size?: 'md' | 'lg'
    variant?: 'primary' | 'secondary'
  } & (
    | {
      type: 'multiple'
      value?: string[] | null
    }
    | {
      type: 'single'
      value?: string | null
    }
  )

  let {
    children,
    name,
    type,
    size = 'md',
    variant = 'primary',
    value = $bindable(),
    ...rest
  }: Props = $props()

  const valueStore: ToggleGroupContext['selected'] = writable(value)
  const nameStore: ToggleGroupContext['name'] = writable()
  const typeStore: ToggleGroupContext['type'] = writable()

  setContext<ToggleGroupContext>('toggle-group', {
    selected: valueStore,
    name: nameStore,
    type: typeStore,
  })

  valueStore.subscribe((newValue) => {
    value = newValue
  })

  $effect(() => {
    if (value) {
      valueStore.set(value)
    }
  })

  $effect(() => {
    nameStore.set(name)
  })

  $effect(() => {
    typeStore.set(type)
  })
</script>

<div {...rest} class={['container', size, variant]}>
  {@render children?.()}
</div>

<style lang="scss">
  .container {
    background-color: var(--toggle-group-background);
    border-radius: 0.25rem;
    display: flex;
    align-items: center;
    width: fit-content;

    &.lg :global {
      label {
        border-radius: 0.25rem;
        min-height: 2rem;
        min-width: 2rem;
        width: 2rem;
        height: 2rem;
        padding: 0;

        svg {
          height: 1.125rem;
          width: 1.125rem;
        }
      }
    }

    &.secondary :global {
      background-color: hsla(0, 0%, 94%, 1);

      label {
        &[aria-selected='true'] {
          background-color: #000;
          color: #fff;
        }
      }
    }
  }
</style>
